import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useQueryClient } from '@tanstack/react-query'
import { Button } from '@/components/ui/Button'
import { Input } from '@/components/ui/Input'
import { supabase } from '@/lib/supabase'

/** Coach: add a new athlete (+ optional login), then jump to assigning them a program. */
export function NewAthleteDialog({ onClose }: { onClose: () => void }) {
  const navigate = useNavigate()
  const qc = useQueryClient()
  const [name, setName] = useState('')
  const [email, setEmail] = useState('')
  const [wantLogin, setWantLogin] = useState(false)
  const [password, setPassword] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const create = async () => {
    if (!name.trim()) return
    setBusy(true)
    setError(null)
    try {
      const { data } = await supabase.auth.getSession()
      const token = data.session?.access_token
      const res = await fetch('/api/create-athlete', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: 'Bearer ' + token },
        body: JSON.stringify({
          name: name.trim(),
          email: email.trim() || undefined,
          password: wantLogin ? password : undefined,
        }),
      })
      const json = await res.json().catch(() => ({}))
      if (!res.ok) throw new Error(json.error || 'Could not create athlete')
      await qc.invalidateQueries({ queryKey: ['athletes'] })
      const id = json.athlete?.id
      if (id) navigate(`/athlete/${id}`)
      onClose()
    } catch (e) {
      setError((e as Error).message)
    } finally {
      setBusy(false)
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4" onClick={onClose}>
      <div
        className="w-full max-w-md rounded-card border border-border bg-surface p-5 shadow-card-hover animate-scale-in"
        onClick={(e) => e.stopPropagation()}
      >
        <h2 className="font-display text-2xl tracking-wide">New Athlete</h2>

        <label className="mb-1.5 mt-4 block font-condensed text-[11px] uppercase tracking-[0.2em] text-muted">Name</label>
        <Input autoFocus value={name} onChange={(e) => setName(e.target.value)} placeholder="Full name" />

        <label className="mb-1.5 mt-4 block font-condensed text-[11px] uppercase tracking-[0.2em] text-muted">Email (optional)</label>
        <Input type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="athlete@email.com" />

        <label className="mt-4 flex items-center gap-2 text-sm text-muted">
          <input type="checkbox" checked={wantLogin} onChange={(e) => setWantLogin(e.target.checked)} className="h-4 w-4 accent-gold" />
          Give them a login now (so they can use the app on their phone)
        </label>

        {wantLogin && (
          <>
            <label className="mb-1.5 mt-3 block font-condensed text-[11px] uppercase tracking-[0.2em] text-muted">Temporary password</label>
            <div className="flex gap-2">
              <Input value={password} onChange={(e) => setPassword(e.target.value)} placeholder="At least 6 characters" />
              <Button variant="secondary" size="md" onClick={() => setPassword('FZ' + Math.floor(1000 + Math.random() * 9000))}>
                Generate
              </Button>
            </div>
            <p className="mt-1.5 text-xs text-subtle">Share this with the athlete — they sign in with their email + this password.</p>
          </>
        )}

        {error && <p className="mt-3 text-sm text-danger">{error}</p>}

        <div className="mt-6 flex gap-2">
          <Button variant="secondary" onClick={onClose} disabled={busy}>Cancel</Button>
          <Button
            className="flex-1"
            disabled={busy || !name.trim() || (wantLogin && (!email.trim() || password.length < 6))}
            onClick={create}
          >
            {busy ? 'Creating…' : 'Create & assign program'}
          </Button>
        </div>
      </div>
    </div>
  )
}
